import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { itemsAPI } from '../../api/itemsAPI';
import { cancelPendingUpload, isAbortError, uploadPendingImage } from '../../utils/pendingImageUploads';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ListingPhotosGuidance from '../../components/items/ListingPhotosGuidance';
import ListingItemFlawsField from '../../components/items/ListingItemFlawsField';
import ImageCropModal from '../../components/common/ImageCropModal';
import PhotoSlotGrid from '../../components/items/PhotoSlotGrid';
import { normalizeListingItemFlaws } from '../../constants/listingItemFlaws';
import {
  getListingSizeColorAttributes,
  buildSizeColorPayload,
  parseAvailableSizes,
} from '../../constants/listingSizeColorAttributes';
import SEO from 'components/seo/SEO';
import styled from 'styled-components';
import { getItemPublicPath, getEditListingPath } from '../../utils/itemLinks';
import { sanitizeDecimalInput } from '../../utils/sanitizeText';
import {
  isContactOnlyListingCategory,
  isElectronicsListingCategory,
} from '../../constants/contactOnlyCategories';
import WeddingEssentialsPolicy from '../../components/items/WeddingEssentialsPolicy';
import { isWeddingEssentialsListing } from '../../components/items/ListingDetailShared';
import {
  WEDDING_CATEGORIES,
  WEDDING_PRE_LOVED_CATEGORY_DATA,
  getWeddingCategoryDepartment,
  expandWeddingCategoryQueryValues,
} from '../../constants/weddingCategories';

// Reuse styled components from CreateItemPage
const CreateContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 100vh;
  background: #a8e6cf;
`;

const CreateCard = styled.div`
  background-color: #a8e6cf;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  padding: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
  `}
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-family: inherit;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
  `}
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
  `}
`;

const ErrorText = styled.span.attrs({ className: 'field-error-text' })`
  color: #dc3545;
  font-size: 0.875rem;
`;

const ChipOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-radius: 0.5rem;
  border: 1.5px solid ${props => props.$active ? '#10b981' : '#e5e7eb'};
  background-color: ${props => props.$active ? '#ecfdf5' : '#ffffff'};
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.$active ? '#065f46' : '#4b5563'};

  &:hover {
    border-color: #10b981;
    background-color: #f0fdf4;
  }

  input {
    accent-color: #10b981;
    width: 1rem;
    height: 1rem;
  }
`;

const AttributeHint = styled.p`
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin: 0 0 0.75rem 0;
  line-height: 1.45;
`;

const SizeGroupBlock = styled.div`
  margin-bottom: 1rem;
`;

const SizeGroupLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
  background-color: var(--bg-secondary);
  border-radius: 0.375rem;
  overflow: hidden;
  
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(220, 53, 69, 1);
  }
`;

const CropImageButton = styled.button`
  position: absolute;
  left: 0.4rem;
  bottom: 0.4rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  border: none;
  background: rgba(16, 185, 129, 0.95);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.25);

  &:hover:not(:disabled) {
    background: #059669;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const SubmitButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerZone = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #fecaca;
`;

const DeleteListingButton = styled.button`
  width: 100%;
  background-color: #fff;
  color: #b91c1c;
  border: 1px solid #fecaca;
  padding: 0.875rem 1.5rem;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #fef2f2;
    border-color: #f87171;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DefectedToggleBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  background: ${props => props.$active ? '#fff3cd' : 'var(--bg-secondary)'};
  border: 1px solid ${props => props.$active ? '#ffc107' : 'var(--border-color)'};
  border-radius: 0.5rem;
  transition: background 0.2s, border-color 0.2s;
`;

const DefectedToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};

  input[type="checkbox"] {
    width: 1.1rem;
    height: 1.1rem;
    accent-color: #e6a817;
    cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
    flex-shrink: 0;
  }
`;

const DefectedBadge = styled.span`
  display: inline-block;
  background: #ffc107;
  color: #5a3e00;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DefectedNote = styled.p`
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
`;

const EditItemPage = ({ weddingPreLovedMode = false }) => {
  const { itemRef } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [item, setItem] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [itemFlaws, setItemFlaws] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [cropModal, setCropModal] = useState(null); // { kind, index?, id?, src, fileName }
  const [cropQueue, setCropQueue] = useState([]);
  const [cropLoadingId, setCropLoadingId] = useState(null);
  const [isWeddingListing, setIsWeddingListing] = useState(weddingPreLovedMode);
  const photoInputRef = useRef(null);
  const [waitingUploads, setWaitingUploads] = useState(false);
  const imageIdRef = useRef(0);
  const uploadControllersRef = useRef(new Map()); // imageId -> AbortController
  const uploadPromisesRef = useRef(new Map()); // imageId -> Promise

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    subcategory: '',
    book_subcategory: '',
    condition: '',
    fragile: false,
    weight_over_15kg: false,
    is_defected: false,
    phone_discoverable: '',
    is_hijabi: '',
    color: '',
  });
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [showDefectedConfirm, setShowDefectedConfirm] = useState(false);
  const [phoneDiscoverConfirm, setPhoneDiscoverConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [errors, setErrors] = useState({});

  const weddingMode = weddingPreLovedMode || isWeddingListing;

  const weddingCategories = WEDDING_CATEGORIES.map((c) => c.apiCategory);
  const categories = weddingMode
    ? Array.from(
        new Set([
          ...weddingCategories,
          ...(formData.category ? expandWeddingCategoryQueryValues(formData.category) : []),
          ...(formData.category ? [formData.category] : []),
        ]),
      )
    : [];

  const weddingSubcategories = weddingMode
    ? (WEDDING_PRE_LOVED_CATEGORY_DATA[formData.category] || [])
    : [];

  const bookSubcategories = [
    'Fiction', 'Self-Development', 'Academic & Education', 'Business & Economics',
    'Technology & Computer Science', 'Medicine & Health', 'Arts & Humanities',
    'Religion & Philosophy', 'Children & Young Adult', 'Reference & Exams',
  ];

  const conditions = [
    'Excellent',
    'New',
    'Like New',
    'Good',
    'Fair',
    'Used',
    'Poor',
  ];

  useEffect(() => {
    const loadItem = async () => {
      if (!itemRef || !user) return;
      
      try {
        setLoading(true);
        const itemData = await itemsAPI.getItemByRef(itemRef);
        
        // Check ownership
        if (itemData.owner_id !== user.id) {
          toast.error(t('listing.onlyOwnItems'));
          navigate('/my-catalogue');
          return;
        }
        
        setItem(itemData);
        const weddingListing = weddingPreLovedMode || isWeddingEssentialsListing(itemData);
        setIsWeddingListing(weddingListing);

        // Old bookmarks to /edit-item/... for wedding rows should land on the wedding editor.
        if (weddingListing && !weddingPreLovedMode) {
          navigate(getEditListingPath(itemData), { replace: true });
          return;
        }

        let description = itemData.description || '';
        let subcategory = itemData.subcategory || '';
        if (weddingListing && description.startsWith('Type: ')) {
          const match = description.match(/^Type:\s*(.+?)\n\n([\s\S]*)$/);
          if (match) {
            if (!subcategory) subcategory = match[1].trim();
            description = match[2];
          }
        }

        setFormData({
          title: itemData.title || '',
          description,
          price: parseFloat(itemData.price).toFixed(2) || '',
          category: itemData.category || '',
          subcategory,
          book_subcategory: itemData.book_subcategory || '',
          condition: itemData.condition || '',
          fragile: Boolean(itemData.fragile),
          weight_over_15kg: Boolean(itemData.weight_over_15kg),
          is_defected: Boolean(itemData.is_defected),
          phone_discoverable: isElectronicsListingCategory(itemData.category)
            ? 'true'
            : isContactOnlyListingCategory(itemData.category)
            ? (itemData.phone_discoverable ? 'true' : 'false')
            : '',
          is_hijabi: itemData.is_hijabi === true ? 'true' : itemData.is_hijabi === false ? 'false' : '',
          color: itemData.color || '',
        });
        setSelectedSizes(parseAvailableSizes(itemData.available_sizes, itemData.size));
        setPhoneDiscoverConfirm(
          isElectronicsListingCategory(itemData.category) || Boolean(itemData.phone_discoverable)
        );
        setItemFlaws(normalizeListingItemFlaws(itemData.item_flaws));
        setExistingImages(itemData.images || []);
      } catch (error) {
        console.error('Error loading item:', error);
        toast.error(t('listing.loadItemFailed'));
        navigate('/my-catalogue');
      } finally {
        setLoading(false);
      }
    };

    loadItem();
  }, [itemRef, user, navigate, weddingPreLovedMode, t]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = t('listing.titleRequired');
    } else if (formData.title.trim().length < 3) {
      newErrors.title = t('listing.titleTooShort');
    } else if (formData.title.trim().length > 200) {
      newErrors.title = t('listing.titleTooLong');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('listing.descriptionRequired');
    } else if (formData.description.trim().length < 25) {
      newErrors.description = t('listing.descriptionTooShort');
    } else if (formData.description.trim().length > 5000) {
      newErrors.description = t('listing.descriptionTooLong');
    }

    if (!formData.price.trim()) {
      newErrors.price = t('listing.priceRequired');
    } else {
      const price = parseFloat(formData.price);
      if (!Number.isFinite(price) || price < 0) {
        newErrors.price = t('listing.invalidPriceBody');
      }
    }

    if (isElectronicsListingCategory(formData.category)) {
      if (!(user?.phone_number || '').trim()) {
        newErrors.phone_discoverable = t('listing.phoneRequiredElectronics');
      } else if (!phoneDiscoverConfirm) {
        newErrors.phone_discoverable = t('listing.phoneConfirmRequired');
      }
    } else if (isContactOnlyListingCategory(formData.category)) {
      if (formData.phone_discoverable === '') {
        newErrors.phone_discoverable = t('listing.phoneDiscoveryRequired');
      } else if (formData.phone_discoverable === 'true') {
        if (!(user?.phone_number || '').trim()) {
          newErrors.phone_discoverable = t('listing.phoneRequiredBeforeSharing');
        } else if (!phoneDiscoverConfirm) {
          newErrors.phone_discoverable = t('listing.phoneConfirmRequired');
        }
      }
    }

    if (!formData.category) {
      newErrors.category = t('listing.categoryRequired');
    }

    if (
      weddingMode
      && (WEDDING_PRE_LOVED_CATEGORY_DATA[formData.category] || []).length > 0
      && !formData.subcategory
    ) {
      newErrors.subcategory = t('listing.subcategoryRequired');
    }

    if (formData.category === 'Books' && !formData.book_subcategory) {
      newErrors.book_subcategory = t('listing.bookSubcategoryRequired');
    }

    if (!formData.condition) {
      newErrors.condition = t('listing.conditionRequired');
    }

    const attrs = getListingSizeColorAttributes(formData.category);
    if (attrs) {
      if (!formData.color) {
        newErrors.color = t('listing.colorRequired');
      }
      if (attrs.requiresSize && selectedSizes.length === 0) {
        newErrors.sizes = t('listing.sizeRequiredBody');
      }
    }

    // Check if at least 3 images will remain
    const remainingImages = existingImages.filter(img => !removedImageIds.includes(img.id)).length + newImages.length;
    if (remainingImages < 3) {
      newErrors.images = t('listing.minImagesRequired');
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const toggleSize = (sizeValue) => {
    setSelectedSizes((prev) =>
      prev.includes(sizeValue) ? prev.filter((s) => s !== sizeValue) : [...prev, sizeValue]
    );
    if (errors.sizes) {
      setErrors((prev) => ({ ...prev, sizes: '' }));
    }
  };

  const listingAttrs = getListingSizeColorAttributes(formData.category);

  const handleImageChange = (e) => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      toast.error(t('listing.imageFilesOnly'));
      return;
    }

    const validFiles = [];
    for (const file of imageFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(t('listing.unsupportedFormat', { name: file.name }));
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('listing.imageTooLarge', { name: file.name }));
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    const startIndex = newImages.length;
    const newImagePreviews = validFiles.map((file) => ({
      id: `${Date.now()}_${++imageIdRef.current}`,
      file,
      preview: URL.createObjectURL(file),
      upload: { status: 'pending', progress: 0 },
    }));

    setNewImages((prev) => [...prev, ...newImagePreviews]);

    const queue = newImagePreviews.map((_, i) => startIndex + i);
    const [first, ...rest] = queue;
    setCropQueue(rest);
    setCropModal({
      kind: 'new',
      index: first,
      src: newImagePreviews[0].preview,
      fileName: newImagePreviews[0].file?.name || 'photo.jpg',
    });
  };

  const openCropForNewIndex = (index) => {
    const image = newImages[index];
    if (!image?.preview) return;
    setCropQueue([]);
    setCropModal({
      kind: 'new',
      index,
      src: image.preview,
      fileName: image.file?.name || `photo_${index + 1}.jpg`,
    });
  };

  const openCropForExisting = async (img) => {
    if (!img?.image) return;
    try {
      setCropLoadingId(img.id);
      const res = await fetch(img.image, { mode: 'cors' });
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const preview = URL.createObjectURL(blob);
      setCropQueue([]);
      setCropModal({
        kind: 'existing',
        id: img.id,
        src: preview,
        fileName: `item_${img.id}.jpg`,
        revokeSrc: preview,
      });
    } catch (err) {
      console.error(err);
      toast.error(t('listing.cropLoadFailed'));
    } finally {
      setCropLoadingId(null);
    }
  };

  const advanceCropQueue = (images, queue) => {
    if (!queue.length) {
      setCropModal(null);
      setCropQueue([]);
      return;
    }
    const [index, ...rest] = queue;
    const image = images[index];
    if (!image?.preview) {
      advanceCropQueue(images, rest);
      return;
    }
    setCropQueue(rest);
    setCropModal({
      kind: 'new',
      index,
      src: image.preview,
      fileName: image.file?.name || `photo_${index + 1}.jpg`,
    });
  };

  const updateNewImageUpload = (id, patch) => {
    setNewImages((prev) =>
      prev.map((img) => (img.id === id ? { ...img, upload: { ...img.upload, ...patch } } : img)),
    );
  };

  // Starts (or restarts) the pick-time upload for one new image. The photo
  // uploads to R2 right away; keys are attached to the listing on save.
  const startNewImageUpload = (image, { fresh = false } = {}) => {
    if (!image?.file || !image?.id) return Promise.resolve({ skipped: true });
    uploadControllersRef.current.get(image.id)?.abort();
    const begin = async () => {
      if (fresh) {
        await cancelPendingUpload(image.id);
      }
      const controller = new AbortController();
      uploadControllersRef.current.set(image.id, controller);
      updateNewImageUpload(image.id, { status: 'uploading', progress: 0, key: undefined, error: undefined });
      const promise = uploadPendingImage({
        file: image.file,
        imageId: image.id,
        signal: controller.signal,
        onProgress: (label, meta) => {
          if (typeof meta?.percent === 'number') {
            updateNewImageUpload(image.id, { status: 'uploading', progress: meta.percent });
          }
        },
      })
        .then((result) => {
          uploadControllersRef.current.delete(image.id);
          if (result?.deferred) {
            updateNewImageUpload(image.id, { status: 'deferred', progress: 100 });
            return { deferred: true };
          }
          updateNewImageUpload(image.id, { status: 'done', progress: 100, key: result.key });
          return { key: result.key };
        })
        .catch((err) => {
          uploadControllersRef.current.delete(image.id);
          if (isAbortError(err)) return { aborted: true };
          const msg = err?.response?.data?.error || err?.message || 'upload failed';
          updateNewImageUpload(image.id, { status: 'error', error: String(msg) });
          return { error: msg };
        });
      return promise;
    };
    const run = begin();
    uploadPromisesRef.current.set(image.id, run);
    return run;
  };

  const retryNewImageUpload = (index) => {
    const image = newImages[index];
    if (image) {
      void startNewImageUpload(image);
    }
  };

  const applyCropResult = ({ file, preview }) => {
    if (!cropModal) return;
    if (cropModal.kind === 'new') {
      const { index } = cropModal;
      let nextImages = newImages;
      let croppedEntry = null;
      setNewImages((prev) => {
        const next = [...prev];
        const old = next[index];
        if (old?.preview) URL.revokeObjectURL(old.preview);
        croppedEntry = {
          id: old?.id || `${Date.now()}_${++imageIdRef.current}`,
          file,
          preview,
          upload: { status: 'pending', progress: 0 },
        };
        next[index] = croppedEntry;
        nextImages = next;
        return next;
      });
      if (croppedEntry) {
        void startNewImageUpload(croppedEntry, { fresh: true });
      }
      advanceCropQueue(
        nextImages.map((img, i) => (i === index ? croppedEntry || img : img)),
        cropQueue,
      );
      return;
    }
    if (cropModal.kind === 'existing') {
      setRemovedImageIds((prev) =>
        prev.includes(cropModal.id) ? prev : [...prev, cropModal.id],
      );
      const entry = {
        id: `${Date.now()}_${++imageIdRef.current}`,
        file,
        preview,
        upload: { status: 'pending', progress: 0 },
      };
      setNewImages((prev) => [...prev, entry]);
      void startNewImageUpload(entry);
      if (cropModal.revokeSrc) URL.revokeObjectURL(cropModal.revokeSrc);
    }
    setCropModal(null);
    setCropQueue([]);
  };

  const skipCrop = () => {
    if (cropModal?.kind === 'existing' && cropModal.revokeSrc) {
      URL.revokeObjectURL(cropModal.revokeSrc);
    }
    if (cropModal?.kind === 'new') {
      const image = newImages[cropModal.index];
      if (image && (!image.upload || image.upload.status === 'pending')) {
        void startNewImageUpload(image);
      }
      advanceCropQueue(newImages, cropQueue);
      return;
    }
    setCropModal(null);
    setCropQueue([]);
  };

  const removeExistingImage = (imageId) => {
    setRemovedImageIds((prev) => [...prev, imageId]);
  };

  const removeNewImage = (index) => {
    const target = newImages[index];
    if (target?.id) {
      uploadControllersRef.current.get(target.id)?.abort();
      uploadControllersRef.current.delete(target.id);
      uploadPromisesRef.current.delete(target.id);
      void cancelPendingUpload(target.id);
    }
    setNewImages((prev) => {
      const next = [...prev];
      if (next[index]?.preview) URL.revokeObjectURL(next[index].preview);
      next.splice(index, 1);
      return next;
    });
    setCropModal((modal) => {
      if (!modal || modal.kind !== 'new') return modal;
      if (modal.index === index) return null;
      if (modal.index > index) return { ...modal, index: modal.index - 1 };
      return modal;
    });
  };

  const handleDeleteListing = async () => {
    if (!item?.id) return;
    const confirmed = window.confirm(
      t('listing.deleteConfirm', { title: item.title })
    );
    if (!confirmed) return;
    try {
      setDeleting(true);
      await itemsAPI.deleteItem(item.id);
      toast.success(t('listing.itemDeleted'));
      navigate('/my-catalogue');
    } catch (err) {
      const message =
        err?.response?.data?.error || t('listing.deleteFailed');
      toast.error(typeof message === 'string' ? message : t('listing.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    const missingFieldMessages = Object.values(validationErrors).filter(Boolean);
    if (missingFieldMessages.length > 0) {
      toast.error(
        <div>
          <strong>{t('listing.missingFieldsToast')}</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
            {missingFieldMessages.slice(0, 6).map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
            {missingFieldMessages.length > 6 && (
              <li>{t('listing.missingFieldsMore', { count: missingFieldMessages.length - 6 })}</li>
            )}
          </ul>
        </div>
      );
      setTimeout(() => {
        document.querySelector('.field-error-text')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setSaving(true);

    try {
      // Update item details
      const itemData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        category: formData.category,
        condition: formData.condition,
        fragile: Boolean(formData.fragile),
        weight_over_15kg: Boolean(formData.weight_over_15kg),
        is_defected: weddingMode ? false : formData.is_defected,
        item_flaws: itemFlaws,
        phone_discoverable: isElectronicsListingCategory(formData.category)
          ? true
          : isContactOnlyListingCategory(formData.category)
          ? formData.phone_discoverable === 'true'
          : false,
        ...(weddingMode && formData.is_hijabi !== ''
          ? { is_hijabi: formData.is_hijabi === 'true' }
          : {}),
        ...buildSizeColorPayload(formData.color, selectedSizes),
      };

      if (formData.category === 'Books' && formData.book_subcategory) {
        itemData.book_subcategory = formData.book_subcategory;
      }

      if (weddingMode) {
        const department = getWeddingCategoryDepartment(formData.category);
        if (department) {
          itemData.department = department;
        }
        if (formData.subcategory) {
          itemData.subcategory = formData.subcategory;
          itemData.description = `Type: ${formData.subcategory}\n\n${itemData.description}`;
        }
      }

      await itemsAPI.updateItemByRef(itemRef, itemData);

      // Remove deleted images
      for (const imageId of removedImageIds) {
        try {
          await itemsAPI.removeItemImage(item.id, imageId);
        } catch (error) {
          console.error(`Error removing image ${imageId}:`, error);
        }
      }

      // Add new images — they were uploaded at pick time; wait for any still in
      // flight, then attach their keys. Deferred ones (proxy storage) upload now.
      if (newImages.length > 0) {
        newImages
          .filter((img) => !img.upload || img.upload.status === 'pending')
          .forEach((img) => { void startNewImageUpload(img); });
        const inFlight = newImages
          .map((img) => uploadPromisesRef.current.get(img.id))
          .filter(Boolean);
        if (inFlight.length > 0) {
          setWaitingUploads(true);
          try {
            await Promise.all(inFlight);
          } finally {
            setWaitingUploads(false);
          }
        }
        const outcomes = await Promise.all(
          newImages.map((img) => uploadPromisesRef.current.get(img.id) || Promise.resolve(null)),
        );
        const failedPhotos = [];
        const orderedKeys = [];
        const deferredFiles = [];
        newImages.forEach((img, i) => {
          const result = outcomes[i];
          if (result?.key) orderedKeys.push(result.key);
          else if (result?.deferred) deferredFiles.push(img.file);
          else failedPhotos.push(img);
        });
        if (failedPhotos.length > 0) {
          toast.error(t('listing.photosUploadFailedRetry', { count: failedPhotos.length }));
          return;
        }
        if (orderedKeys.length > 0) {
          await itemsAPI.attachPendingImages(item.id, orderedKeys);
        }
        if (deferredFiles.length > 0) {
          const formDataToSend = new FormData();
          deferredFiles.forEach((file) => {
            formDataToSend.append('images', file);
          });
          await itemsAPI.addItemImages(item.id, formDataToSend);
        }
      }

      toast.success(t('listing.itemUpdated'));
      navigate(getItemPublicPath({ title: formData.title, category: formData.category }), {
        state: { itemId: item.id },
      });
    } catch (error) {
      console.error('Error updating item:', error);
      const errorMessage = error.response?.data?.error || t('listing.updateFailed');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <CreateContainer>
        <LoadingSpinner />
      </CreateContainer>
    );
  }

  if (!item) {
    return (
      <CreateContainer>
        <CreateCard>
          <PageTitle>{t('listing.itemNotFound')}</PageTitle>
        </CreateCard>
      </CreateContainer>
    );
  }

  const remainingExistingImages = existingImages.filter(img => !removedImageIds.includes(img.id));

  return (
    <>
      <SEO
        title={weddingMode ? t('listing.seoEditWeddingTitle') : t('listing.seoEditTitle', { title: item.title })}
        description={
          weddingMode
            ? t('listing.seoEditWeddingDescription')
            : t('listing.seoEditDescription')
        }
      />
      <CreateContainer>
        <CreateCard>
          <PageTitle>
            {weddingMode
              ? t('listing.editWeddingTitle')
              : t('listing.editTitle')}
          </PageTitle>
          {weddingMode ? <WeddingEssentialsPolicy /> : null}
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="title">{t('listing.title')} *</Label>
              <Input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                error={errors.title}
              />
              {errors.title && <ErrorText>{errors.title}</ErrorText>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">{t('listing.description')} *</Label>
              <TextArea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={6}
                error={errors.description}
              />
              {errors.description && <ErrorText>{errors.description}</ErrorText>}
            </FormGroup>

            <ListingItemFlawsField value={itemFlaws} onChange={setItemFlaws} />

            <FormGroup>
              <Label htmlFor="price">{t('listing.price')} *</Label>
              <Input
                id="price"
                type="text"
                inputMode="decimal"
                dir="ltr"
                value={formData.price}
                onChange={(e) => handleInputChange('price', sanitizeDecimalInput(e.target.value))}
                error={errors.price}
                placeholder={t('listing.pricePlaceholder')}
              />
              {errors.price && <ErrorText>{errors.price}</ErrorText>}
            </FormGroup>

            {isElectronicsListingCategory(formData.category) ? (
              <FormGroup>
                <Label>{t('listing.contactOnly')}</Label>
                <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.5, margin: '0 0 0.75rem' }}>
                  {t('listing.contactOnlyElectronicsBody')}
                </p>
                <Label>{t('listing.buyerContact')} *</Label>
                <div style={{ marginTop: '0.5rem', padding: '0.85rem 1rem', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#334155' }}>
                    {t('listing.confirmPhoneNumber')}
                  </p>
                  <p style={{ margin: '0 0 0.65rem', fontWeight: 700, color: '#0f172a' }}>
                    {user?.phone_number || t('listing.noPhoneOnProfile')}
                  </p>
                  {(user?.phone_number || '').trim() ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={phoneDiscoverConfirm}
                        onChange={(e) => setPhoneDiscoverConfirm(e.target.checked)}
                        style={{ width: 16, height: 16 }}
                      />
                      {t('listing.showNumberToBuyers')}
                    </label>
                  ) : (
                    <ErrorText>{t('listing.addPhoneForElectronics')}</ErrorText>
                  )}
                </div>
                {errors.phone_discoverable && <ErrorText>{errors.phone_discoverable}</ErrorText>}
              </FormGroup>
            ) : isContactOnlyListingCategory(formData.category) ? (
              <FormGroup>
                <Label>{t('listing.contactOnly')}</Label>
                <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.5, margin: '0 0 0.75rem' }}>
                  {t('listing.contactOnlyBody')}
                </p>
                <Label>{t('listing.phoneDiscovery')} *</Label>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="phone_discoverable"
                      value="true"
                      checked={formData.phone_discoverable === 'true'}
                      onChange={(e) => {
                        handleInputChange('phone_discoverable', e.target.value);
                        setPhoneDiscoverConfirm(false);
                      }}
                    />
                    {t('listing.yes')}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="phone_discoverable"
                      value="false"
                      checked={formData.phone_discoverable === 'false'}
                      onChange={(e) => {
                        handleInputChange('phone_discoverable', e.target.value);
                        setPhoneDiscoverConfirm(false);
                      }}
                    />
                    {t('listing.no')}
                  </label>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.45, margin: '0.75rem 0 0' }}>
                  {t('listing.phoneDiscoveryHint')}
                </p>
                {formData.phone_discoverable === 'true' && (
                  <div style={{ marginTop: '0.85rem', padding: '0.85rem 1rem', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#334155' }}>
                      {t('listing.phoneShowConfirmQuestion')}
                    </p>
                    <p style={{ margin: '0 0 0.65rem', fontWeight: 700, color: '#0f172a' }}>
                      {user?.phone_number || t('listing.noPhoneOnProfile')}
                    </p>
                    {(user?.phone_number || '').trim() ? (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={phoneDiscoverConfirm}
                          onChange={(e) => setPhoneDiscoverConfirm(e.target.checked)}
                          style={{ width: 16, height: 16 }}
                        />
                        {t('listing.showNumberToBuyers')}
                      </label>
                    ) : null}
                  </div>
                )}
                {errors.phone_discoverable && <ErrorText>{errors.phone_discoverable}</ErrorText>}
              </FormGroup>
            ) : null}

            {weddingMode ? (
            <FormGroup>
              <Label htmlFor="category">{t('listing.category')} *</Label>
              <Select
                id="category"
                value={formData.category}
                onChange={(e) => {
                  handleInputChange('category', e.target.value);
                  handleInputChange('subcategory', '');
                  handleInputChange('color', '');
                  setSelectedSizes([]);
                }}
                error={errors.category}
              >
                <option value="">{t('listing.selectCategory')}</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Select>
              {errors.category && <ErrorText>{errors.category}</ErrorText>}
            </FormGroup>
            ) : formData.category ? (
              <FormGroup>
                <Label>{t('listing.category')}</Label>
                <AttributeHint>{t('listing.category')}: {formData.category}</AttributeHint>
              </FormGroup>
            ) : null}

            {weddingMode && weddingSubcategories.length > 0 ? (
              <FormGroup>
                <Label htmlFor="subcategory">{t('listing.type')} *</Label>
                <Select
                  id="subcategory"
                  value={formData.subcategory}
                  onChange={(e) => handleInputChange('subcategory', e.target.value)}
                  error={errors.subcategory}
                >
                  <option value="">{t('listing.selectType')}</option>
                  {weddingSubcategories.map((sub) => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                  {formData.subcategory && !weddingSubcategories.includes(formData.subcategory) ? (
                    <option value={formData.subcategory}>{t('listing.currentOption', { value: formData.subcategory })}</option>
                  ) : null}
                </Select>
                {errors.subcategory && <ErrorText>{errors.subcategory}</ErrorText>}
              </FormGroup>
            ) : null}

            {weddingMode && (
              <FormGroup>
                <Label>{t('listing.hijabiQuestion')}</Label>
                <Select
                  value={formData.is_hijabi}
                  onChange={(e) => handleInputChange('is_hijabi', e.target.value)}
                >
                  <option value="">{t('listing.select')}</option>
                  <option value="true">{t('listing.yesHijabi')}</option>
                  <option value="false">{t('listing.noNonHijabi')}</option>
                </Select>
              </FormGroup>
            )}

            {formData.category === 'Computers & Electronics' && (
              <FormGroup>
                <Label>{t('listing.importantNotice')}</Label>
                <p style={{ fontSize: '0.9rem', color: '#1e40af', lineHeight: 1.5, margin: '0 0 0.75rem', padding: '0.75rem 0.9rem', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 12 }}>
                  {t('listing.electronicsDisclaimer')}
                </p>
              </FormGroup>
            )}

            {listingAttrs && (
              <>
                <FormGroup>
                  <Label>{t('listing.color')} *</Label>
                  <AttributeHint>{t('listing.colorHint')}</AttributeHint>
                  <Select
                    name="edit-color"
                    value={formData.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    error={errors.color}
                  >
                    <option value="">{t('listing.selectColor')}</option>
                    {listingAttrs.colors.map((colorOption) => (
                      <option key={colorOption} value={colorOption}>{colorOption}</option>
                    ))}
                    {formData.color && !listingAttrs.colors.includes(formData.color) ? (
                      <option value={formData.color}>{t('listing.currentValueOption', { value: formData.color })}</option>
                    ) : null}
                  </Select>
                  {errors.color && <ErrorText>{errors.color}</ErrorText>}
                </FormGroup>

                {listingAttrs.requiresSize && listingAttrs.sizeProfile && (
                  <FormGroup>
                    <Label>{listingAttrs.sizeProfile.label}(s) * — {t('listing.selectOneOrMore')}</Label>
                    <AttributeHint>{listingAttrs.sizeProfile.hint}</AttributeHint>
                    {listingAttrs.sizeProfile.groups.map((group) => (
                      <SizeGroupBlock key={group.label || 'default'}>
                        {group.label ? <SizeGroupLabel>{group.label}</SizeGroupLabel> : null}
                        <ChipGrid>
                          {group.sizes.map((sizeOption) => (
                            <ChipOption
                              key={`${group.label}-${sizeOption}`}
                              $active={selectedSizes.includes(sizeOption)}
                            >
                              <input
                                type="checkbox"
                                checked={selectedSizes.includes(sizeOption)}
                                onChange={() => toggleSize(sizeOption)}
                              />
                              {sizeOption}
                            </ChipOption>
                          ))}
                        </ChipGrid>
                      </SizeGroupBlock>
                    ))}
                    {errors.sizes && <ErrorText>{errors.sizes}</ErrorText>}
                  </FormGroup>
                )}
              </>
            )}

            {formData.category === 'Books' && (
              <FormGroup>
                <Label htmlFor="book_subcategory">{t('listing.bookSubcategory')} *</Label>
                <Select
                  id="book_subcategory"
                  value={formData.book_subcategory}
                  onChange={(e) => handleInputChange('book_subcategory', e.target.value)}
                  error={errors.book_subcategory}
                >
                  <option value="">{t('listing.selectSubcategory')}</option>
                  {bookSubcategories.map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </Select>
                {errors.book_subcategory && <ErrorText>{errors.book_subcategory}</ErrorText>}
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="condition">{t('listing.condition')} *</Label>
              <Select
                id="condition"
                value={formData.condition}
                onChange={(e) => handleInputChange('condition', e.target.value)}
                error={errors.condition}
              >
                <option value="">{t('listing.selectCondition')}</option>
                {conditions.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Select>
              {errors.condition && <ErrorText>{errors.condition}</ErrorText>}
            </FormGroup>

            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={formData.fragile}
                  onChange={(e) => handleInputChange('fragile', e.target.checked)}
                  style={{
                    marginRight: '0.5rem',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    verticalAlign: 'middle',
                  }}
                />
                {t('listing.fragileLabel')}
              </Label>
            </FormGroup>

            {isContactOnlyListingCategory(formData.category) && (
              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={formData.weight_over_15kg}
                    onChange={(e) => handleInputChange('weight_over_15kg', e.target.checked)}
                    style={{
                      marginRight: '0.5rem',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                    }}
                  />
                  {t('listing.weightOver15')}
                </Label>
              </FormGroup>
            )}

            {/* Defected toggle — only shown for regular items; defected items show a read-only badge */}
            {!weddingMode && formData.is_defected ? (
              <DefectedToggleBox $active>
                <DefectedToggleRow $disabled>
                  <input type="checkbox" checked readOnly disabled />
                  <Label style={{ margin: 0 }}>
                    {t('listing.listedInRecycling')}&nbsp;<DefectedBadge>{t('listing.defected')}</DefectedBadge>
                  </Label>
                </DefectedToggleRow>
                <DefectedNote>
                  {t('listing.defectedReadOnlyNote')}
                </DefectedNote>
              </DefectedToggleBox>
            ) : !weddingMode ? (
              <DefectedToggleBox $active={showDefectedConfirm}>
                <DefectedToggleRow
                  onClick={() => {
                    const next = !showDefectedConfirm;
                    setShowDefectedConfirm(next);
                    handleInputChange('is_defected', next);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showDefectedConfirm}
                    onChange={() => {
                      const next = !showDefectedConfirm;
                      setShowDefectedConfirm(next);
                      handleInputChange('is_defected', next);
                    }}
                  />
                  <Label style={{ margin: 0, cursor: 'pointer' }}>
                    {t('listing.moveToRecycling')}
                  </Label>
                </DefectedToggleRow>
                {showDefectedConfirm && (
                  <DefectedNote>
                    ♻️ {t('listing.recyclingReclassify')} <strong>{t('listing.defectedInline')}</strong> {t('listing.recyclingMove')} <strong>{t('listing.changeCannotBeUndone')}</strong>
                  </DefectedNote>
                )}
              </DefectedToggleBox>
            ) : null}

            <ListingPhotosGuidance photoCount={remainingExistingImages.length + newImages.length} />

            <FormGroup>
              <Label>{t('listing.images')}</Label>
              {remainingExistingImages.length > 0 && (
                <ImagePreviewGrid>
                  {remainingExistingImages.map((img) => (
                    <ImagePreview key={img.id}>
                      <img src={img.image} alt={t('listing.itemImageAlt')} />
                      <RemoveImageButton
                        type="button"
                        onClick={() => removeExistingImage(img.id)}
                        title={t('listing.removeImage')}
                      >
                        ×
                      </RemoveImageButton>
                      <CropImageButton
                        type="button"
                        disabled={cropLoadingId === img.id}
                        onClick={() => openCropForExisting(img)}
                      >
                        {cropLoadingId === img.id ? '…' : t('listing.editImage')}
                      </CropImageButton>
                    </ImagePreview>
                  ))}
                </ImagePreviewGrid>
              )}

              <PhotoSlotGrid
                items={newImages}
                maxSlots={Math.max(4, 12 - remainingExistingImages.length)}
                onPickSlot={() => photoInputRef.current?.click()}
                onRemove={removeNewImage}
                onEdit={openCropForNewIndex}
                onRetry={retryNewImageUpload}
              />
              <Input
                ref={photoInputRef}
                id="edit-images"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              {errors.images && <ErrorText>{errors.images}</ErrorText>}
            </FormGroup>

            {cropModal ? (
              <ImageCropModal
                imageSrc={cropModal.src}
                fileName={cropModal.fileName}
                remainingAfter={cropQueue.length}
                onCancel={skipCrop}
                onComplete={applyCropResult}
              />
            ) : null}

            <SubmitButton type="submit" disabled={saving || deleting}>
              {waitingUploads ? t('listing.finishingUploads') : saving ? t('listing.saving') : t('listing.saveChanges')}
            </SubmitButton>

            <DangerZone>
              <p style={{ margin: '0 0 0.75rem', color: '#7f1d1d', fontSize: '0.9rem' }}>
                {t('listing.deleteListingHint')}
              </p>
              <DeleteListingButton
                type="button"
                disabled={saving || deleting}
                onClick={handleDeleteListing}
              >
                {deleting ? t('listing.deleting') : t('listing.deleteListing')}
              </DeleteListingButton>
            </DangerZone>
          </Form>
        </CreateCard>
      </CreateContainer>
    </>
  );
};

export default EditItemPage;
